/**
 * Calc Helper - CSS Custom Properties untuk nilai vw
 * Mengkonversi nilai seperti --vw-69 menjadi calc(69 / 1440 * 100vw)
 *
 * FORMAT BARU (RECOMMENDED - sederhana, hanya nilai angka):
 * =========================================================
 * Desktop:
 *   padding-top: var(--vw-53);           // calc(53 / 1440 * 100vw)
 *   padding-right: var(--vw-60);         // calc(60 / 1440 * 100vw)
 *   padding-bottom: var(--vw-40);        // calc(40 / 1440 * 100vw)
 *   padding-left: var(--vw-20);          // calc(20 / 1440 * 100vw)
 *   padding: var(--vw-69);               // calc(69 / 1440 * 100vw)
 *   margin-top: var(--vw-110);           // calc(110 / 1440 * 100vw)
 *   margin-right: var(--vw-30);          // calc(30 / 1440 * 100vw)
 *   margin-bottom: var(--vw-20);         // calc(20 / 1440 * 100vw)
 *   margin-left: var(--vw-10);           // calc(10 / 1440 * 100vw)
 *   top: var(--vw-0);                    // calc(0 / 1440 * 100vw)
 *   right: var(--vw-30);                 // calc(30 / 1440 * 100vw)
 *   bottom: var(--vw-45);                // calc(45 / 1440 * 100vw)
 *   left: var(--vw-n3);                  // calc(-3 / 1440 * 100vw) - nilai negatif
 *   width: var(--vw-30);                 // calc(30 / 1440 * 100vw)
 *   height: var(--vw-470);               // calc(470 / 1440 * 100vw)
 *   gap: var(--vw-15);                   // calc(15 / 1440 * 100vw)
 *   font-size: var(--vw-18);             // calc(18 / 1440 * 100vw)
 *
 * Mobile (prefix m-vw-):
 *   padding: var(--m-vw-30);             // calc(30 / 414 * 100vw)
 *   right: var(--m-vw-30);                // calc(30 / 414 * 100vw)
 *   bottom: var(--m-vw-20);               // calc(20 / 414 * 100vw)
 *   font-size: var(--m-vw-18);            // calc(18 / 414 * 100vw)
 *   left: var(--m-vw-n30);                // calc(-30 / 414 * 100vw) - nilai negatif
 *
 * FORMAT LAMA (BACKWARD COMPATIBLE):
 * ===================================
 *   padding-top: var(--pt-53);           // Desktop: calc(53 / 1440 * 100vw)
 *   padding: var(--p-69);                // Desktop: calc(69 / 1440 * 100vw)
 *   margin-top: var(--mt-110);           // Desktop: calc(110 / 1440 * 100vw)
 *   bottom: var(--bottom-45);             // Desktop: calc(45 / 1440 * 100vw)
 *   left: var(--left-n3);                 // Desktop: calc(-3 / 1440 * 100vw)
 *   padding: var(--m-p-30);               // Mobile: calc(30 / 414 * 100vw)
 *   right: var(--m-right-30);             // Mobile: calc(30 / 414 * 100vw)
 */

(function () {
    "use strict";

    const DESKTOP_BASE = 1440;
    const MOBILE_BASE = 414;

    /**
     * Scan semua style tags dan stylesheets untuk menemukan custom properties yang digunakan
     * Support format baru (--vw-*) dan format lama (--*) untuk backward compatibility
     */
    function scanAndGenerateProperties() {
        const root = document.documentElement;
        const usedValues = new Set(); // Store hanya nilai, tanpa property name (format baru)
        const legacyProperties = new Set(); // Store property yang digunakan di format lama untuk backward compatibility

        // Compiled regex untuk performance (format baru: --vw-{value} dan --m-vw-{value})
        // Format: var(--vw-69), var(--vw-22_5) desimal pakai underscore, var(--vw-n40)
        const vwPattern = /var\(--(m-)?vw-(n?\d+(?:[._]\d+)?)\)/g;
        const vwMatch = /var\(--(m-)?vw-(n?\d+(?:[._]\d+)?)\)/;

        // Compiled regex untuk backward compatibility (format lama: --{property}-{value})
        // Format: var(--pt-53), var(--m-p-30), var(--bottom-n40)
        const legacyPattern = /var\(--(m-)?([a-z]+)-(n?\d+(?:[._]\d+)?)\)/g;
        const legacyMatch = /var\(--(m-)?([a-z]+)-(n?\d+(?:[._]\d+)?)\)/;

        /**
         * Parse match format baru (hanya nilai, tanpa property name)
         */
        function parseVWMatch(match) {
            const propMatch = match.match(vwMatch);
            if (propMatch) {
                const isMobile = propMatch[1] === "m-";
                let valueStr = propMatch[2];
                // Handle nilai negatif dengan prefix 'n'
                const isNegative = valueStr.startsWith("n");
                if (isNegative) {
                    valueStr = valueStr.substring(1);
                }
                valueStr = valueStr.replace(/_/g, ".");
                const value = isNegative
                    ? -parseFloat(valueStr)
                    : parseFloat(valueStr);
                return { isMobile, value };
            }
            return null;
        }

        /**
         * Parse match format lama (dengan property name untuk backward compatibility)
         */
        function parseLegacyMatch(match) {
            const propMatch = match.match(legacyMatch);
            if (propMatch) {
                const isMobile = propMatch[1] === "m-";
                const property = propMatch[2];
                let valueStr = propMatch[3];
                // Handle nilai negatif dengan prefix 'n'
                const isNegative = valueStr.startsWith("n");
                if (isNegative) {
                    valueStr = valueStr.substring(1);
                }
                valueStr = valueStr.replace(/_/g, ".");
                const value = isNegative
                    ? -parseFloat(valueStr)
                    : parseFloat(valueStr);
                // Track property yang digunakan untuk backward compatibility
                legacyProperties.add(
                    JSON.stringify({ isMobile, property, value }),
                );
                return { isMobile, value };
            }
            return null;
        }

        // Scan style tags
        const styleTags = Array.from(document.querySelectorAll("style"));
        styleTags.forEach(function (styleTag) {
            const cssText = styleTag.textContent || styleTag.innerHTML || "";

            // Scan format baru (--vw-{value}) - hanya nilai, tanpa property name
            const vwMatches = cssText.match(vwPattern);
            if (vwMatches) {
                vwMatches.forEach(function (match) {
                    const parsed = parseVWMatch(match);
                    if (parsed) {
                        usedValues.add(JSON.stringify(parsed));
                    }
                });
            }

            // Scan format lama (backward compatible: --{property}-{value})
            const legacyMatches = cssText.match(legacyPattern);
            if (legacyMatches) {
                legacyMatches.forEach(function (match) {
                    // Skip jika sudah match dengan format baru (--vw-*)
                    if (!match.includes("vw-")) {
                        const parsed = parseLegacyMatch(match);
                        if (parsed) {
                            usedValues.add(JSON.stringify(parsed));
                        }
                    }
                });
            }
        });

        function isReadableStyleSheet(sheet) {
            if (!sheet || sheet.disabled) return false;
            if (!sheet.href) return true; // inline <style>
            try {
                const url = new URL(sheet.href, window.location.href);
                return url.origin === window.location.origin;
            } catch (e) {
                return false;
            }
        }

        // Scan stylesheets (untuk external CSS files)
        const styleSheets = Array.from(document.styleSheets);
        styleSheets.forEach(function (sheet) {
            if (!isReadableStyleSheet(sheet)) return;
            try {
                const rules = sheet.cssRules || sheet.rules || [];
                rules.forEach(function (rule) {
                    if (rule.cssText) {
                        const cssText = rule.cssText;

                        // Scan format baru (--vw-{value})
                        const vwMatches = cssText.match(vwPattern);
                        if (vwMatches) {
                            vwMatches.forEach(function (match) {
                                const parsed = parseVWMatch(match);
                                if (parsed) {
                                    usedValues.add(JSON.stringify(parsed));
                                }
                            });
                        }

                        // Scan format lama (backward compatible)
                        const legacyMatches = cssText.match(legacyPattern);
                        if (legacyMatches) {
                            legacyMatches.forEach(function (match) {
                                // Skip jika sudah match dengan format baru (--vw-*)
                                if (!match.includes("vw-")) {
                                    const parsed = parseLegacyMatch(match);
                                    if (parsed) {
                                        usedValues.add(JSON.stringify(parsed));
                                    }
                                }
                            });
                        }
                    }
                });
            } catch (e) {
                // Cross-origin stylesheet, skip
                if (
                    window.location.hostname === "localhost" ||
                    window.location.hostname === "127.0.0.1"
                ) {
                    console.debug(
                        "Skipped cross-origin stylesheet:",
                        sheet.href,
                    );
                }
            }
        });

        // Cache untuk properties yang sudah di-generate (performance optimization)
        const generatedProperties = new Set();

        // Generate format baru (--vw-{value}) - RECOMMENDED - hanya nilai, tanpa property name
        usedValues.forEach(function (valueStr) {
            const parsed = JSON.parse(valueStr);
            const base = parsed.isMobile ? MOBILE_BASE : DESKTOP_BASE;
            const calcValue = `calc(${parsed.value} / ${base} * 100vw)`;
            // Format property name: desimal pakai underscore (titik invalid di CSS ident)
            let valueStrFormatted =
                parsed.value < 0
                    ? "n" + Math.abs(parsed.value)
                    : parsed.value.toString();
            valueStrFormatted = valueStrFormatted.replace(".", "_");

            const vwPropName = parsed.isMobile
                ? `--m-vw-${valueStrFormatted}`
                : `--vw-${valueStrFormatted}`;

            // Set format baru (jika belum di-generate)
            if (!generatedProperties.has(vwPropName)) {
                root.style.setProperty(vwPropName, calcValue);
                generatedProperties.add(vwPropName);
            }
        });

        // Generate format lama untuk backward compatibility (hanya property yang benar-benar digunakan)
        legacyProperties.forEach(function (propStr) {
            const prop = JSON.parse(propStr);
            const base = prop.isMobile ? MOBILE_BASE : DESKTOP_BASE;
            const calcValue = `calc(${prop.value} / ${base} * 100vw)`;
            let valueStrFormatted =
                prop.value < 0
                    ? "n" + Math.abs(prop.value)
                    : prop.value.toString();
            valueStrFormatted = valueStrFormatted.replace(".", "_");

            const legacyPropName = prop.isMobile
                ? `--m-${prop.property}-${valueStrFormatted}`
                : `--${prop.property}-${valueStrFormatted}`;

            // Set format lama untuk backward compatibility (jika belum di-generate)
            if (!generatedProperties.has(legacyPropName)) {
                root.style.setProperty(legacyPropName, calcValue);
                generatedProperties.add(legacyPropName);
            }
        });
    }

    let scanDebounceTimer = null;
    let lastScanInnerWidth = null;

    /**
     * Debounced scan — menggabungkan pemanggilan berurutan (resize, MutationObserver, late CSS).
     */
    function scheduleScan() {
        if (scanDebounceTimer) clearTimeout(scanDebounceTimer);
        scanDebounceTimer = setTimeout(function () {
            scanDebounceTimer = null;
            scanAndGenerateProperties();
        }, 50);
    }

    function watchStylesheetLinks() {
        document.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
            if (link.dataset.vwScanWatched === "true") return;
            link.dataset.vwScanWatched = "true";
            if (!link.sheet) {
                link.addEventListener("load", scheduleScan, { once: true });
            }
        });
    }

    function observeDynamicStyles() {
        if (typeof MutationObserver === "undefined") return;

        const observer = new MutationObserver(function (mutations) {
            for (let i = 0; i < mutations.length; i++) {
                const mutation = mutations[i];
                if (!mutation.addedNodes || !mutation.addedNodes.length) continue;

                for (let j = 0; j < mutation.addedNodes.length; j++) {
                    const node = mutation.addedNodes[j];
                    if (node.nodeType !== 1) continue;

                    const tag = node.tagName;
                    if (tag === "STYLE" || (tag === "LINK" && node.rel === "stylesheet")) {
                        scheduleScan();
                        return;
                    }

                    if (node.querySelector && node.querySelector("style, link[rel='stylesheet']")) {
                        scheduleScan();
                        return;
                    }
                }
            }
        });

        observer.observe(document.documentElement, {
            childList: true,
            subtree: true,
        });
    }

    /**
     * Initialize
     */
    function init() {
        lastScanInnerWidth = window.innerWidth;

        // Scan styles setelah DOM ready
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", function () {
                scheduleScan();
                watchStylesheetLinks();
            });
        } else {
            scheduleScan();
            watchStylesheetLinks();
        }

        observeDynamicStyles();

        // Re-scan setelah window load (late CSS, section {% style %}, search page assets)
        window.addEventListener("load", function () {
            scheduleScan();
            watchStylesheetLinks();
            // Fallback untuk style yang di-inject setelah load (mis. Shopify section styles)
            setTimeout(scheduleScan, 250);
        });

        // Re-scan saat lebar viewport berubah (breakpoint), bukan saat DevTools hanya mengubah tinggi
        let resizeTimer;
        window.addEventListener(
            "resize",
            function () {
                const currentWidth = window.innerWidth;
                if (currentWidth === lastScanInnerWidth) return;
                lastScanInnerWidth = currentWidth;

                clearTimeout(resizeTimer);
                resizeTimer = setTimeout(scheduleScan, 100);
            },
            { passive: true },
        );

        document.addEventListener("shopify:section:load", scheduleScan);
    }

    window.dbodhiVwScan = scheduleScan;

    // Start
    init();
})();
